import { z } from 'zod';

import { paginated } from '../../api/pagination';

/**
 * Message templates — `GET /api/message-templates` and friends.
 *
 * Two kinds of template share one list: the two the app sends on its own (the
 * roster message and the manager report — rewordable, never deleted) and the
 * coordinators' own, sent from an event as a notice.
 *
 * Responses are snake_case, request bodies camelCase — the same split as staff.
 */
export const MESSAGE_TEMPLATE_KINDS = ['roster_message', 'manager_report', 'custom'] as const;
export type MessageTemplateKind = (typeof MESSAGE_TEMPLATE_KINDS)[number];

export const MESSAGE_FILL_INS = [
  'name',
  'event',
  'date',
  'time',
  'shifts',
  'link',
  'roster',
] as const;
export type MessageFillIn = (typeof MESSAGE_FILL_INS)[number];

/** Lenient: a fill-in this build does not know is dropped, not a parse failure. */
const fillInsSchema = z
  .array(z.string())
  .transform((names) =>
    names.filter((name): name is MessageFillIn =>
      (MESSAGE_FILL_INS as readonly string[]).includes(name),
    ),
  );

export const messageTemplateSchema = z
  .object({
    id: z.string(),
    kind: z.enum(MESSAGE_TEMPLATE_KINDS),
    name: z.string(),
    body: z.string(),
    is_default: z.boolean(),
    fill_ins: fillInsSchema,
    required_fill_ins: fillInsSchema,
    updated_at: z.string().nullable(),
  })
  .transform((raw) => ({
    id: raw.id,
    kind: raw.kind,
    name: raw.name,
    body: raw.body,
    /** A built-in nobody has edited — the app's own wording. */
    isDefault: raw.is_default,
    /** What this template may use, and must — the SERVER's rules for its kind. */
    fillIns: raw.fill_ins,
    requiredFillIns: raw.required_fill_ins,
    updatedAt: raw.updated_at,
  }));

export type MessageTemplate = z.infer<typeof messageTemplateSchema>;

export const messageTemplateListSchema = paginated(messageTemplateSchema);
export type MessageTemplateList = z.infer<typeof messageTemplateListSchema>;

export interface FillInRules {
  allowed: readonly MessageFillIn[];
  required: readonly MessageFillIn[];
}

/**
 * What a coordinator's own message may use — a custom template, or one typed
 * on the spot.
 *
 * DUPLICATED from the server's rules on purpose (see the boundary note in
 * AGENTS.md): the server validates and fills; this only stops a `{typo}` at
 * the form and draws the chips. A built-in's rules come from the server on the
 * template itself.
 */
export const NOTICE_FILL_IN_RULES: FillInRules = {
  allowed: ['name', 'event', 'date', 'time'],
  required: [],
};

export const TEMPLATE_NAME_MAX = 60;
export const TEMPLATE_BODY_MAX = 2000;

const FILL_IN_PATTERN = /\{([a-z_]+)\}/g;

/** Every `{fill-in}` a body names, once each, in order. */
export function fillInsIn(body: string): string[] {
  return [...new Set([...body.matchAll(FILL_IN_PATTERN)].map((match) => match[1] ?? ''))];
}

/** What is wrong with a body under these rules — the server's own wording. Empty when fine. */
export function messageBodyProblems(body: string, rules: FillInRules): string[] {
  const named = fillInsIn(body);
  const unknown = named.filter((name) => !(rules.allowed as readonly string[]).includes(name));
  const missing = rules.required.filter((name) => !named.includes(name));

  return [
    ...unknown.map(
      (name) =>
        `{${name}} is not a fill-in here. Use ${rules.allowed.map((fillIn) => `{${fillIn}}`).join(', ')}.`,
    ),
    ...missing.map((name) => `This message must include {${name}}.`),
  ];
}

/**
 * The body with its fill-ins filled — for a PREVIEW. What is actually sent is
 * filled by the server, per person; a fill-in with no value here is left as is.
 */
export function fillMessage(body: string, values: Partial<Record<MessageFillIn, string>>): string {
  return body.replace(FILL_IN_PATTERN, (match, name: string) => {
    const value = values[name as MessageFillIn];
    return value === undefined ? match : value;
  });
}

const bodySchema = z
  .string()
  .trim()
  .min(1, 'Write the message.')
  .max(TEMPLATE_BODY_MAX, `Keep the message to ${String(TEMPLATE_BODY_MAX)} characters.`);

const nameSchema = z
  .string()
  .trim()
  .min(1, 'Give the template a name.')
  .max(TEMPLATE_NAME_MAX, `Keep the name to ${String(TEMPLATE_NAME_MAX)} characters.`);

/**
 * A body schema under some rules, with every problem on `body` — `superRefine`
 * with a path, so the error lands under the message box, not the form root.
 */
export function messageBodySchema(rules: FillInRules) {
  return z.object({ body: bodySchema }).superRefine((value, ctx) => {
    for (const message of messageBodyProblems(value.body, rules)) {
      ctx.addIssue({ code: 'custom', path: ['body'], message });
    }
  });
}

/** `POST /api/message-templates` — a custom template. */
export const createMessageTemplateInputSchema = z
  .object({ name: nameSchema, body: bodySchema })
  .superRefine((value, ctx) => {
    for (const message of messageBodyProblems(value.body, NOTICE_FILL_IN_RULES)) {
      ctx.addIssue({ code: 'custom', path: ['body'], message });
    }
  });

export type CreateMessageTemplateInput = z.input<typeof createMessageTemplateInputSchema>;

/**
 * `PATCH /api/message-templates/:id`. A built-in takes `body` only; its fill-in
 * rules are the template's own, so the caller validates with
 * `messageBodySchema(template)` before sending.
 */
export const updateMessageTemplateInputSchema = z.object({
  name: nameSchema.optional(),
  body: bodySchema.optional(),
});

export type UpdateMessageTemplateInput = z.input<typeof updateMessageTemplateInputSchema>;
